import React from 'react'
import PropTypes from 'prop-types'
import {
    Button, Input, Link, Typography
} from '@telus/koodo-ui-kit'
import ReCAPTCHA from 'react-google-recaptcha'

/**
 * EmailStep — first step of the verify flow.
 *
 * Renders the email input form. All business logic (API calls, recaptcha
 * execution, error resolution) is owned by VerifyPage and passed in as props. 
 */
const EmailStep = ({
    title,
    infoText,
    infoSubText,
    infoHighlightText,
    emailOptions,
    selectedEmailOption,
    onEmailOptionChange,
    isEmailReadOnly,
    isGeneratingEmail,
    generatingLabel,
    emailLabel,
    emailPlaceholder,
    submitLabel,
    backLabel,
    email,
    onEmailChange,
    onEmailBlur,
    onEmailKeyDown,
    onSubmit,
    onBack,
    isLoading,
    emailError,
    serverError,
    serverErrorText,
    captchaError,
    captchaErrorText,
    recaptchaRef,
    handleRecaptcha,
    siteKey,
    emailCharMaxLimit,
    containerRef
}) => {
    const feedbackText = emailError || (serverError ? serverErrorText : '') || (captchaError ? captchaErrorText : '')
    const validationState = feedbackText ? 'error' : undefined
    const showEmailOptions = emailOptions.length > 0

    // While an address is being minted the field stands empty behind the
    // "Generating.." hint, and the kit's `loading` state blocks the submit.
    const resolvedPlaceholder = (isGeneratingEmail && generatingLabel)
        || (isEmailReadOnly ? '' : emailPlaceholder)

    return (
        <div ref={containerRef} className="flex flex-col w-full mx-auto gap-6">
            <Typography heading="h1" variant={{ size: 'h3' }} block>
                {title}
            </Typography>

            {(infoText || infoHighlightText) && (
                <Typography variant={{ size: 'medium' }} block>
                    {infoText}
                    {infoText && infoHighlightText && ' '}
                    {/* Dealer guest flow reassurance — flows inline with the info copy. */}
                    {infoHighlightText && (
                        <span className="text-brand-purple font-bold">{infoHighlightText}</span>
                    )}
                </Typography>
            )}

            {infoSubText && (
                <Typography variant={{ size: 'medium' }} block>
                    {infoSubText}
                </Typography>
            )}

            {/* Dealer guest flow only — consumer renders no options and is unchanged. */}
            {showEmailOptions && (
                <div className="email-entry-options -mb-2">
                    <Input
                        label=""
                        type="radio"
                        name="email-entry-option"
                        options={emailOptions}
                        value={selectedEmailOption}
                        onChange={onEmailOptionChange}
                        testId="email-entry-options"
                    />
                </div>
            )}

            <Input
                label={emailLabel}
                type="email"
                value={email}
                placeholder={resolvedPlaceholder}
                onChange={onEmailChange}
                onBlur={onEmailBlur}
                onKeyDown={onEmailKeyDown}
                onSubmit={onSubmit}
                onSubmitEditing={onSubmit}
                maxLength={emailCharMaxLimit}
                // `disabled`, not `readOnly` — the kit accepts a readOnly prop on text
                // inputs but never forwards it, leaving the generated address fully
                // editable. Its `disabled` maps to the DOM `readonly` attribute (not
                // `disabled`), so the value still submits and stays full-contrast black,
                // while the grey fill and not-allowed cursor show the field is fixed.
                disabled={isEmailReadOnly}
                feedback={feedbackText}
                validation={validationState}
                testId="email-input"
            />

            {/* Invisible reCAPTCHA — only mounts when a siteKey is configured */}
            {siteKey && (
                <div className="flex w-full items-center justify-center">
                    <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={siteKey}
                        size="invisible"
                        onChange={handleRecaptcha}
                        onExpired={() => recaptchaRef.current?.reset()}
                    />
                </div>
            )}

            <Button
                onPress={onSubmit}
                variant={{ width: 'full' }}
                loading={isLoading || isGeneratingEmail}
                loadingLabel="Loading"
                testId="email-submit"
            >
                {submitLabel}
            </Button>

            <div className="text-center">
                <Link
                    href="/"
                    onClick={(e) => { e.preventDefault(); onBack() }}
                    testId="email-go-back"
                    tokens={{ blockFontSize: 18, lineHeight: 28 / 18, fontWeight: 600 }}
                >
                    {backLabel}
                </Link>
            </div>
        </div>
    )
}

EmailStep.propTypes = {
    title: PropTypes.string.isRequired,
    infoText: PropTypes.string,
    infoHighlightText: PropTypes.string,
    emailOptions: PropTypes.arrayOf(PropTypes.shape({
        title: PropTypes.string,
        value: PropTypes.string
    })),
    selectedEmailOption: PropTypes.string,
    onEmailOptionChange: PropTypes.func,
    isEmailReadOnly: PropTypes.bool,
    isGeneratingEmail: PropTypes.bool,
    generatingLabel: PropTypes.string,
    emailLabel: PropTypes.string.isRequired,
    emailPlaceholder: PropTypes.string,
    submitLabel: PropTypes.string.isRequired,
    backLabel: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    onEmailChange: PropTypes.func.isRequired,
    onEmailBlur: PropTypes.func,
    onEmailKeyDown: PropTypes.func,
    onSubmit: PropTypes.func.isRequired,
    onBack: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
    emailError: PropTypes.string,
    serverError: PropTypes.bool,
    serverErrorText: PropTypes.string,
    captchaError: PropTypes.bool,
    captchaErrorText: PropTypes.string,
    recaptchaRef: PropTypes.shape({}),
    handleRecaptcha: PropTypes.func,
    siteKey: PropTypes.string,
    emailCharMaxLimit: PropTypes.number,
    containerRef: PropTypes.shape({})
}

EmailStep.defaultProps = {
    onEmailBlur: () => { },
    onEmailKeyDown: () => { },
    infoText: '',
    infoHighlightText: '',
    emailOptions: [],
    selectedEmailOption: '',
    onEmailOptionChange: () => { },
    isEmailReadOnly: false,
    isGeneratingEmail: false,
    generatingLabel: '',
    emailPlaceholder: '',
    isLoading: false,
    emailError: '',
    serverError: false,
    serverErrorText: '',
    captchaError: false,
    captchaErrorText: '',
    recaptchaRef: null,
    handleRecaptcha: () => { },
    siteKey: '',
    emailCharMaxLimit: 100,
    containerRef: null
}

export default EmailStep
